import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import type { CommentReaction } from '@tourfold/libraries/tf-api-client-public-v2';
import { ReactIconComponent } from './react-icon.component';

/**
 * Per-comment chip row showing existing emoji reactions. Slack / GitHub / Linear pattern: click a
 * chip to toggle the calling user's reaction for that emoji. The "+" button at the end opens the
 * picker — owned by the parent {@link CommentRowComponent} so the same picker instance is shared
 * between this row and the toolbar's "React" action.
 *
 * The bar renders **only when there is at least one reaction**. With zero reactions the comment
 * has no chips to surface, so the row collapses entirely; the user's entry point becomes the
 * "React" button in the actions toolbar (which is itself hover-gated on mouse devices and always
 * visible on touch).
 *
 * Purely presentational — no optimistic state. The parent owns the rollback / aggregate-replace
 * lifecycle in `<tf-comments>`.
 */
@Component({
  selector: 'tf-reactions-bar',
  standalone: true,
  imports: [TranslateModule, ReactIconComponent],
  templateUrl: './reactions-bar.component.html',
  styleUrl: './reactions-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReactionsBarComponent {
  /** Per-emoji aggregate for the row, in server-side insertion order. */
  readonly reactions = input.required<readonly CommentReaction[]>();

  /**
   * When false, both the chip click and the "+" button become inert (and the "+" button is hidden).
   * Used to suppress interactivity on tombstoned comments and on unauthenticated reads. The chips
   * themselves still render so the count is visible.
   */
  readonly canReact = input<boolean>(true);

  /** Emitted when the user clicks an existing chip. The parent decides add vs remove. */
  readonly reactionToggled = output<string>();

  /** Emitted when the user clicks the "+" button — the parent opens its picker anchored to the event. */
  readonly addReactionRequested = output<Event>();

  protected onChipClick(emoji: string): void {
    if (!this.canReact()) return;
    this.reactionToggled.emit(emoji);
  }

  protected onAddClick(event: Event): void {
    if (!this.canReact()) return;
    this.addReactionRequested.emit(event);
  }
}
